/**
 * v2-API-Adapter für das BK-Anfragen-Modul (Betreuungskräfte-Pipeline).
 *
 * Das Backend stellt parallel zu den PB-Endpoints BK-Varianten mit
 * identischem JSON-Shape bereit:
 *
 *   GET /v2/admin/bk-phases            ↔ GET /v2/admin/phases
 *   GET /v2/admin/bk-phase-rules       ↔ GET /v2/admin/phase-rules
 *   GET /v2/admin/bk-phase-rule-fields ↔ GET /v2/admin/phase-rule-fields
 *
 * Die Typen (PbPhase, PhaseRule, …) werden aus src/lib/v1.ts wiederverwendet,
 * weil die Response-Shapes 1:1 identisch sind. Der BK-Phasenwechsel läuft über
 * die ADDITIVE Backend-Route PATCH /v2/admin/bk-users/{id}/phase (validiert
 * gegen bk_phase_rules, 422 E_PHASE_RULES mit unmet[], Gate users.write) —
 * NICHT über den PB-Endpoint /users/{id}/phase. "Übernehmen" bleibt
 * user-generisch (POST /v2/admin/users/{id}/claim).
 */

import { apiV2 } from './api';
import type { PbPhase, PhaseCreatePayload, PhaseUpdatePayload, PhaseRule, PhaseRuleField, PhaseUpdateResult } from './v1';

/** BK-User-Mutationen — Phasenwechsel gegen die BK-eigene Route. */
export const v2BkUsers = {
  /**
   * PATCH /v2/admin/bk-users/{id}/phase
   * Shape identisch zu v2Users.setPhase; validiert serverseitig gegen
   * bk_phase_rules (422 E_PHASE_RULES mit unmet[]).
   */
  setPhase: async (
    id: number,
    payload: { action: 'advance' } | { phase: number },
  ): Promise<PhaseUpdateResult> => {
    const res = await apiV2.patch<{ data: PhaseUpdateResult }>(
      `/admin/bk-users/${id}/phase`,
      payload,
    );
    return res.data.data;
  },
};

/** BK-Phasen (Board-Spalten der BK-Anfragen-Seite). */
export const v2BkPhases = {
  /**
   * GET /v2/admin/bk/phases → Board-Spalten, lesend (users.read). Die
   * Verwaltungsroute `list` verlangt menu.einstellungen und liefert Mitarbeitern
   * ohne Einstellungs-Recht 403 (Kanban ohne Spalten).
   */
  board: async (): Promise<PbPhase[]> => {
    const res = await apiV2.get<{ data: PbPhase[]; errors: null }>('/admin/bk/phases');
    return res.data.data;
  },

  /** GET /v2/admin/bk-phases → aktive BK-Phasen, sortiert. */
  list: async (): Promise<PbPhase[]> => {
    const res = await apiV2.get<{ data: PbPhase[]; errors: null }>('/admin/bk-phases');
    return res.data.data;
  },

  /** POST /v2/admin/bk-phases — neue BK Stage anlegen. */
  create: async (payload: PhaseCreatePayload): Promise<PbPhase> => {
    const res = await apiV2.post<{ data: PbPhase; errors: null }>('/admin/bk-phases', payload);
    return res.data.data;
  },

  /** PUT /v2/admin/bk-phases/{id} — BK Stage bearbeiten. */
  update: async (id: number, payload: PhaseUpdatePayload): Promise<PbPhase> => {
    const res = await apiV2.put<{ data: PbPhase; errors: null }>(`/admin/bk-phases/${id}`, payload);
    return res.data.data;
  },

  /** DELETE /v2/admin/bk-phases/{id} — BK Stage löschen. */
  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/bk-phases/${id}`);
  },

  /** PUT /v2/admin/bk-phases/reorder — Reihenfolge speichern. */
  reorder: async (ids: number[]): Promise<void> => {
    await apiV2.put('/admin/bk-phases/reorder', { ids });
  },
};

/** BK-Phasen-Regelsystem — identisches Shape wie v2PhaseRules. */
export const v2BkPhaseRules = {
  list: async (): Promise<PhaseRule[]> => {
    const res = await apiV2.get<{ data: PhaseRule[] }>('/admin/bk-phase-rules');
    return res.data.data;
  },

  fields: async (): Promise<PhaseRuleField[]> => {
    const res = await apiV2.get<{ data: PhaseRuleField[] }>('/admin/bk-phase-rule-fields');
    return res.data.data;
  },

  create: async (input: { phase_id: number; field: string; operator: 'not_empty' | 'equals'; value?: string | null }): Promise<PhaseRule> => {
    const res = await apiV2.post<{ data: PhaseRule }>('/admin/bk-phase-rules', input);
    return res.data.data;
  },

  update: async (id: number, input: Partial<{ field: string; operator: 'not_empty' | 'equals'; value: string | null; aktiv: boolean }>): Promise<PhaseRule> => {
    const res = await apiV2.put<{ data: PhaseRule }>(`/admin/bk-phase-rules/${id}`, input);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/bk-phase-rules/${id}`);
  },
};
